/**
 * Unilar UVPs
 * Infinite horizontal marquee (slider mode) + drag carousel (static mode).
 */

class UnilarUvps {
  constructor(root) {
    this.root = root;
    this.enabled = root.dataset.sliderEnabled === 'true';
    this.speed = Number.parseInt(root.dataset.sliderSpeed, 10) || 5;
    this.track = root.querySelector('[data-uvps-track]');
    this.list = root.querySelector('[data-uvps-list]');
    this.viewport = root.querySelector('.unilar-uvps__viewport');
    this.handleResize = this.debounce(this.setupMarquee.bind(this), 200);
    this.drag = {
      active: false,
      moved: false,
      startX: 0,
      scrollLeft: 0,
      pointerId: null,
    };
    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);
    this.boundClickCapture = this.onClickCapture.bind(this);
    this.handleFitCheck = this.debounce(this.updateCenteredState.bind(this), 150);
  }

  init() {
    if (!this.list) return;

    if (!this.enabled) {
      this.root.classList.add('unilar-uvps--static');
      this.root.classList.remove('unilar-uvps--slider');
      this.initDrag();
      this.updateCenteredState();
      window.addEventListener('resize', this.handleFitCheck);
      window.requestAnimationFrame(() => this.updateCenteredState());
      return;
    }

    this.root.classList.add('unilar-uvps--slider');
    this.root.classList.remove('unilar-uvps--static');
    this.setupMarquee();
    window.addEventListener('resize', this.handleResize);
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('resize', this.handleFitCheck);
    this.track?.querySelectorAll('[data-uvps-clone]').forEach((node) => node.remove());
    this.root.classList.remove('is-centered');
    this.root.classList.remove('is-scrollable');
    this.destroyDrag();
  }

  updateCenteredState() {
    if (!(this.viewport instanceof HTMLElement) || !this.list) return;

    // Measure natural width without the centered layout constraints.
    this.root.classList.remove('is-centered');

    const contentWidth = this.list.scrollWidth;
    const availableWidth = this.viewport.clientWidth;
    const fits = contentWidth <= availableWidth - 2;

    this.root.classList.toggle('is-centered', fits);
    this.root.classList.toggle('is-scrollable', !fits);

    if (fits && this.viewport.scrollLeft !== 0) {
      this.viewport.scrollLeft = 0;
    }
  }

  setupMarquee() {
    if (!this.track || !this.list) return;

    this.track.querySelectorAll('[data-uvps-clone]').forEach((node) => node.remove());

    const clone = this.list.cloneNode(true);
    clone.setAttribute('data-uvps-clone', 'true');
    clone.setAttribute('aria-hidden', 'true');
    this.track.appendChild(clone);

    const duration = (11 - this.speed) * 13;
    this.root.style.setProperty('--uvps-duration', `${duration}s`);
  }

  initDrag() {
    if (!(this.viewport instanceof HTMLElement)) return;

    this.viewport.addEventListener('pointerdown', this.boundPointerDown);
    this.viewport.addEventListener('pointermove', this.boundPointerMove);
    this.viewport.addEventListener('pointerup', this.boundPointerUp);
    this.viewport.addEventListener('pointercancel', this.boundPointerUp);
    this.viewport.addEventListener('lostpointercapture', this.boundPointerUp);
    this.viewport.addEventListener('click', this.boundClickCapture, true);
  }

  destroyDrag() {
    if (!(this.viewport instanceof HTMLElement)) return;

    this.viewport.removeEventListener('pointerdown', this.boundPointerDown);
    this.viewport.removeEventListener('pointermove', this.boundPointerMove);
    this.viewport.removeEventListener('pointerup', this.boundPointerUp);
    this.viewport.removeEventListener('pointercancel', this.boundPointerUp);
    this.viewport.removeEventListener('lostpointercapture', this.boundPointerUp);
    this.viewport.removeEventListener('click', this.boundClickCapture, true);
    this.viewport.classList.remove('is-dragging');
  }

  onPointerDown(event) {
    if (!(this.viewport instanceof HTMLElement)) return;
    if (this.root.classList.contains('is-centered')) return;
    if (event.pointerType === 'touch') return;
    if (event.button != null && event.button !== 0) return;

    this.drag.active = true;
    this.drag.moved = false;
    this.drag.startX = event.clientX;
    this.drag.scrollLeft = this.viewport.scrollLeft;
    this.drag.pointerId = event.pointerId;
    this.viewport.classList.add('is-dragging');

    try {
      this.viewport.setPointerCapture(event.pointerId);
    } catch (error) {
      // Capture may fail in some browsers.
    }
  }

  onPointerMove(event) {
    if (!this.drag.active || !(this.viewport instanceof HTMLElement)) return;

    const delta = event.clientX - this.drag.startX;
    if (Math.abs(delta) > 4) {
      this.drag.moved = true;
      event.preventDefault();
    }

    this.viewport.scrollLeft = this.drag.scrollLeft - delta;
  }

  onPointerUp() {
    if (!this.drag.active || !(this.viewport instanceof HTMLElement)) return;

    if (this.drag.pointerId != null) {
      try {
        this.viewport.releasePointerCapture(this.drag.pointerId);
      } catch (error) {
        // Capture may already be released.
      }
    }

    this.drag.active = false;
    this.drag.pointerId = null;
    this.viewport.classList.remove('is-dragging');
  }

  onClickCapture(event) {
    if (!this.drag.moved) return;
    event.preventDefault();
    event.stopPropagation();
    this.drag.moved = false;
  }

  debounce(fn, delay) {
    let timeoutId;

    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => fn(...args), delay);
    };
  }
}

const instances = new WeakMap();

function initUnilarUvps() {
  document.querySelectorAll('[data-unilar-uvps]').forEach((root) => {
    instances.get(root)?.destroy();

    const instance = new UnilarUvps(root);
    instance.init();
    instances.set(root, instance);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUnilarUvps);
} else {
  initUnilarUvps();
}

document.addEventListener('shopify:section:load', initUnilarUvps);
document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.querySelector?.('[data-unilar-uvps]');
  if (!root) return;

  instances.get(root)?.destroy();
  instances.delete(root);
});
